import { cn } from '@april9au/react-common/utils';

import { useStrings } from '@/i18n/use-strings';

/** Where the agency credit is allowed to point. `utm_source` is how April9 attributes it. */
const APRIL9_URL = 'https://april9.com.au?utm_source=beach_passport';

/**
 * The agency credit: "Developed by" and the April9 wordmark, linked out. One
 * component for both places it appears — under the welcome screen's trust line,
 * on navy, and in the content pages' footer, on white.
 *
 * It is deliberately quiet. A credit is not a call to action: 12 px type, a
 * 16 px mark, and 65 % opacity until it is hovered or focused, so it reads as a
 * signature rather than a fourth footer link competing with Privacy and Terms.
 *
 * The wordmark is drawn as a **mask** over `bg-current` rather than as an
 * `<img>`. `public/images/april9.svg` is a single asset that has to sit white on
 * the navy card and grey on the white footer, and an `<img>` cannot inherit the
 * text colour — the file's own `fill="currentColor"` resolves to black there.
 * Masking the alpha channel makes the mark take whatever colour the link is,
 * which is also what keeps hover and focus states honest with one rule.
 */
export function DevelopedBy({ className }: { className?: string }) {
  const { content } = useStrings();

  return (
    <a
      href={APRIL9_URL}
      target="_blank"
      rel="noopener"
      className={cn(
        // `items-baseline`, not `items-center`: the mark is an empty box, so its
        // baseline is its bottom edge — which is where the wordmark's letters
        // actually sit. Centring it instead drops "April" ~3 px below the line
        // and lets the raised 9 float.
        'inline-flex items-baseline gap-1.5 text-xs opacity-65',
        'transition-opacity duration-150 hover:opacity-100 focus-visible:opacity-100 motion-reduce:transition-none',
        'rounded-sm focus-visible:ring-2 focus-visible:ring-current focus-visible:ring-offset-2 focus-visible:outline-none',
        className,
      )}
    >
      {content.footer_developed_by}
      {/* The mark carries the company's name, so it is what the link is called.
          The new-tab note follows it the same way every other external link in
          the app announces one. */}
      <span className="sr-only"> April9 ({content.content_new_tab})</span>
      <span
        aria-hidden
        className="h-4 w-auto bg-current [aspect-ratio:629/333] [mask-image:url(/images/april9.svg)] [mask-position:center] [mask-repeat:no-repeat] [mask-size:contain]"
      />
    </a>
  );
}
